import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { GlowHeart } from "@/pages/HeartChain";
import { useCountdown, pad } from "@/lib/contest";

const KEY = "itz_heart_token";

export function useHeartClaim() {
  const { user } = useAuth();
  const navigate = useNavigate();
  useEffect(() => {
    const t = localStorage.getItem(KEY);
    if (!user || !t) return;
    localStorage.removeItem(KEY);
    api.post(`/heart/accept/${t}`).then(({ data }) => { toast.success(`💖 Du hast das Herz! Du bist Station ${data.station}`); navigate("/herz-kette"); }).catch((e) => toast.error(formatErr(e)));
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps
}

export function HeartClaim() { useHeartClaim(); return null; }

export default function HeartLink() {
  const { token } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get(`/heart/link/${token}`).then(({ data }) => setD(data)).catch(() => setD({ valid: false })); }, [token]);
  const { h, m, s } = useCountdown(d?.deadline);
  const accept = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/heart/accept/${token}`); toast.success(`💖 Du hast das Herz! Station ${data.station}`); navigate("/herz-kette"); }
    catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  const remember = () => localStorage.setItem(KEY, token);
  return (
    <div className="min-h-screen noise-bg flex flex-col items-center px-5 py-8" data-testid="heart-link-page">
      <Logo to="/" />
      {!d ? <Loader2 className="mt-20 w-7 h-7 animate-spin text-rose-400" /> : !d.valid ? (
        <div className="mt-16 text-center space-y-3" data-testid="heart-link-invalid">
          <GlowHeart size="w-24 h-24" beating={false} />
          <p className="font-display text-2xl font-extrabold">Das Herz ist schon weitergewandert</p>
          <p className="text-slate-500">Jemand war schneller – oder die Zeit ist abgelaufen.</p>
          <Link to={user ? "/herz-kette" : "/registrieren"} className="inline-flex h-11 px-6 items-center rounded-full pink-gradient text-white font-bold">{user ? "Zur Herz-Kette" : "Trotzdem mitmachen"}</Link>
        </div>
      ) : (
        <div className="mt-10 w-full max-w-md text-center space-y-5">
          <p className="text-xs font-extrabold uppercase tracking-[0.25em] text-rose-500">Die Herz-Kette #{d.number}</p>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold leading-tight">{(d.from?.name || "Jemand").split(" ")[0]} schenkt dir das Herz 💖</h1>
          <GlowHeart />
          <p className="text-slate-600">{d.length > 0 ? <>Schon <b className="text-rose-600">{d.length} Menschen</b> haben das Herz weitergegeben. Nimm es an, du wirst <b>Station {d.length + 1}</b></> : <>Du wirst die <b className="text-rose-600">allererste Station</b> dieser Kette</>} – gib das Herz dann innerhalb von 24 Stunden weiter, bevor die Kette reißt!</p>
          <p className="text-sm font-bold text-slate-500">Noch {pad(h)}:{pad(m)}:{pad(s)} gültig</p>
          {user ? (
            <button onClick={accept} disabled={busy} data-testid="heart-link-accept" className="w-full h-14 rounded-full pink-gradient text-white text-lg font-extrabold shadow-xl shadow-rose-300 active:scale-95 transition-transform inline-flex items-center justify-center gap-2">{busy && <Loader2 className="w-5 h-5 animate-spin" />} Herz annehmen 💖</button>
          ) : (
            <div className="space-y-2">
              <Link to="/registrieren" onClick={remember} data-testid="heart-link-register" className="w-full h-14 px-6 rounded-full pink-gradient text-white text-base font-extrabold shadow-xl shadow-rose-300 inline-flex items-center justify-center">Kostenlos mitmachen & Herz annehmen</Link>
              <Link to="/login" onClick={remember} data-testid="heart-link-login" className="block text-sm font-bold text-rose-600">Ich habe schon ein Konto</Link>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
